import { useContext, useEffect, useState, type FormEvent } from "react";
import { PickupLocationContext } from "../../../store/PickupLocationContext";
import { UsersContext } from "../../../store/UsersContext";
import { PickupContext } from "../../../store/PickupContext";
import { useTiersWithDisplayName } from "../../../hooks/useTiersWithDisplayName";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";
import { SeasonContext } from "../../../store/SeasonContext";
import {
  PaymentStatus,
  ADMIN_ASSIGNABLE_PAYMENT_STATUS_OPTIONS,
} from "../../../utils/types";
import { userHasSubscriptionHistory } from "../../../../../utils/tierPricing";
import {
  isCsaFarmPickupLocation,
  resolveCsaDeliveryDisplaySek,
} from "../../../../../constants/csaPickup";

export const ADD_CSA_TO_ACCOUNT_FORM_ID = "add-csa-to-account-form";

export type AddCSAToAccountFormProps = {
  userId: string;
  onSuccess?: () => void;
  onError?: (message: string) => void;
  onSubmitChange?: (isSubmitting: boolean) => void;
  /** When false, submit via external button with form={ADD_CSA_TO_ACCOUNT_FORM_ID}. */
  showSubmitButton?: boolean;
};

const AddCSAToAccountForm = ({
  userId,
  onSuccess,
  onError,
  onSubmitChange,
  showSubmitButton = true,
}: AddCSAToAccountFormProps) => {
  const { assignSubscription, users } = useContext(UsersContext);
  const { pickupLocations } = useContext(PickupLocationContext);
  const { refreshPickups } = useContext(PickupContext);
  const { refetch: refetchSubscriptions } = useContext(SubscriptionsContext);
  const { csaDeliveryPrice } = useContext(SeasonContext);
  const { isAuthenticated } = useAuthUser();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus | "">(
    PaymentStatus.Pending,
  );
  const validationMessage =
    "Please fill out default pickup location, pickup day, and subscription type!";

  useEffect(() => {
    onSubmitChange?.(isSubmitting);
  }, [isSubmitting, onSubmitChange]);

  const targetUser = users.find((user) => user.userId === userId);
  const hasHistory = userHasSubscriptionHistory(
    targetUser?.subscriptionHistory,
  );
  const tiers = useTiersWithDisplayName({ hasSubscriptionHistory: hasHistory });
  const locationOptions = pickupLocations.map((location) => (
    <option key={location._id} value={location._id}>
      {location.name}
    </option>
  ));

  const selectedPickupLocation = pickupLocations.find(
    (location) => location._id === selectedLocation,
  );
  const days = selectedPickupLocation?.days;
  const dayOptions = days?.map((day) => (
    <option key={day.day}>{day.day}</option>
  ));

  const deliveryChargeSek = selectedLocation
    ? resolveCsaDeliveryDisplaySek(selectedLocation, csaDeliveryPrice)
    : null;
  const selectedTier = tiers.find((tier) => tier.name === selectedSubscription);
  const needsPaymentCheckout = paymentStatus === PaymentStatus.Pending;

  function getTierId() {
    const tier = tiers.find((tier) => tier.name === selectedSubscription);
    return tier?.id;
  }

  function formIsValid(): boolean {
    return Boolean(
      selectedLocation && selectedDay && selectedSubscription && getTierId(),
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    if (!formIsValid()) {
      setFormError(validationMessage);
      return;
    }

    setFormError(null);

    if (!isAuthenticated) {
      onError?.("You must be logged in (MongoDB) to apply a subscription to an account.");
      return;
    }
    if (paymentStatus === "") {
      onError?.("Please select a payment status.");
      return;
    }
    const tierId = getTierId();
    if (!tierId) {
      onError?.("Please select a subscription type.");
      return;
    }
    if (!selectedTier?.isAvailable) {
      onError?.("This tier is full. Please choose another tier.");
      return;
    }
    setIsSubmitting(true);
    try {
      await assignSubscription(userId, {
        subscriptionTierId: tierId,
        pickupLocationId: selectedLocation,
        pickupDay: selectedDay,
        subscriptionPaymentStatus: paymentStatus,
      });

      await refreshPickups();
      refetchSubscriptions();
      onSuccess?.();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Failed to apply CSA subscription to this account.";
      onError?.(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  const subscriptionOptions = tiers.map((tier) => {
    if (tier.isAvailable) {
      return <option key={tier.id}>{tier.name}</option>;
    }
    return null;
  });

  return (
    <form
      id={ADD_CSA_TO_ACCOUNT_FORM_ID}
      noValidate
      onSubmit={handleSubmit}
      onInput={() => setFormError(null)}
      className="form"
    >
      <div className="dropdown-long-wrapper">
        <label htmlFor="selected-location" className="sr-only">
          Default Pickup Location
        </label>
        <select
          id="selected-location"
          className="dropdown create-dropdown-long"
          value={selectedLocation}
          onChange={(e) => {
            setSelectedLocation(e.target.value);
            setFormError(null);
          }}
        >
          {!selectedLocation && (
            <option value="">Default Pickup Location</option>
          )}
          {locationOptions}
        </select>
        <label htmlFor="selected-day" className="sr-only">
          Default Pickup Day
        </label>
      </div>
      <div className="dropdown-long-wrapper">
        <select
          id="selected-day"
          className="dropdown create-dropdown-long"
          value={selectedDay}
          onChange={(e) => {
            setSelectedDay(e.target.value);
            setFormError(null);
          }}
        >
          {!selectedDay && <option value="">Default Pickup Day</option>}
          {dayOptions}
        </select>
      </div>

      <div className="dropdown-long-wrapper">
        <label htmlFor="selected-subscription" className="sr-only">
          Subscription type
        </label>
        <select
          id="selected-subscription"
          className="dropdown create-dropdown-long"
          value={selectedSubscription}
          onChange={(e) => {
            setSelectedSubscription(e.target.value);
            setFormError(null);
          }}
        >
          {!selectedSubscription && <option value="">Subscription Type</option>}
          {subscriptionOptions}
        </select>
      </div>
      <div className="dropdown-long-wrapper">
        <label htmlFor="payment-status" className="sr-only">
          Payment Status
        </label>
        <select
          id="payment-status"
          className="dropdown create-dropdown-long"
          value={paymentStatus}
          onChange={(e) => {
            setPaymentStatus(e.target.value as PaymentStatus);
            setFormError(null);
          }}
        >
          {!paymentStatus && <option value="">Payment Status</option>}
          {ADMIN_ASSIGNABLE_PAYMENT_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {selectedLocation &&
        deliveryChargeSek !== null &&
        selectedTier &&
        needsPaymentCheckout && (
          <p>
            Payment link includes subscription
            {isCsaFarmPickupLocation(selectedLocation) ? "." : " + delivery."}
          </p>
        )}
      {hasHistory && needsPaymentCheckout && (
        <p>Returning member subscription price applies for this user.</p>
      )}
      {paymentStatus === PaymentStatus.Paid && (
        <p>
          Paid: no Stripe checkout is sent. Collect subscription and delivery
          fees manually if needed.
        </p>
      )}
      {showSubmitButton && (
        <button
          type="submit"
          className="btn btn-add-subscription"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Add subscription to account"}
        </button>
      )}
      {formError && (
        <p className="pickup-form-validation-msg" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
};

export default AddCSAToAccountForm;
